/* eslint-disable @typescript-eslint/no-explicit-any */
import { saveAs } from 'file-saver';
import { AxiosError } from 'axios';

import { apiGet, apiPost } from './apiClient';

const XLSX_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export type MasterEntity =
  | 'service-catalog'
  | 'user'
  | 'coach'
  | 'trial'
  | 'class'
  | 'master-data'
  | 'registration';

const downloadXlsx = async (url: string, filename: string) => {
  // Binary endpoints stream the workbook, so ask axios for a blob.
  const response = await apiGet(url, { responseType: 'blob' });
  saveAs(new Blob([response as any], { type: XLSX_MIME }), filename);
};

export const downloadMasterData = (entity: MasterEntity) =>
  downloadXlsx(`/master-data/${entity}/export`, `${entity}.xlsx`);

export const downloadMasterDataTemplate = (entity: MasterEntity) =>
  downloadXlsx(`/master-data/${entity}/template`, `${entity}_template.xlsx`);

// ---------------------------------------------------------------------------
// Import — preview (dry-run) then commit
// ---------------------------------------------------------------------------

export type ImportRowError = { row: number; messages: string[] };

export type ImportValidation = {
  totalRows: number;
  validRows: number;
  errorCount: number;
  errors: ImportRowError[];
};

export type PreviewPlanRow = {
  row: number;
  action: 'create' | 'update';
  summary: string;
};

export type ImportPreview = {
  mode: 'preview';
  totalRows: number;
  willCreate: number;
  willUpdate: number;
  plan: PreviewPlanRow[];
};

export type ImportCommitSummary = {
  totalRows: number;
  created: number;
  updated: number;
  importId: number;
};

type Envelope<T> = { status: number; message: string; data: T };

export type PreviewOutcome =
  | { ok: true; preview: ImportPreview }
  | { ok: false; validation: ImportValidation };

export type CommitOutcome =
  | { ok: true; summary: ImportCommitSummary }
  | { ok: false; validation: ImportValidation };

/**
 * POST the file for preview or commit. A validation failure (HTTP 422) is
 * thrown by axios; we normalize it into a structured `{ ok: false }` so the page
 * can render per-row errors instead of a generic toast.
 */
const postImport = async <T>(
  entity: MasterEntity,
  file: File,
  mode: 'preview' | 'commit',
): Promise<
  { ok: true; data: T } | { ok: false; validation: ImportValidation }
> => {
  const formData = new FormData();
  formData.append('file', file);
  try {
    const response = await apiPost<Envelope<T>>(
      `/master-data/${entity}/import?mode=${mode}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return { ok: true, data: response.data };
  } catch (error) {
    const axiosError = error as AxiosError<Envelope<ImportValidation>>;
    const body = axiosError.response?.data;
    if (body?.data?.errors) {
      return { ok: false, validation: body.data };
    }
    throw error;
  }
};

export const previewImport = async (
  entity: MasterEntity,
  file: File,
): Promise<PreviewOutcome> => {
  const result = await postImport<ImportPreview>(entity, file, 'preview');
  return result.ok
    ? { ok: true, preview: result.data }
    : { ok: false, validation: result.validation };
};

export const commitImport = async (
  entity: MasterEntity,
  file: File,
): Promise<CommitOutcome> => {
  const result = await postImport<ImportCommitSummary>(entity, file, 'commit');
  return result.ok
    ? { ok: true, summary: result.data }
    : { ok: false, validation: result.validation };
};

// ---------------------------------------------------------------------------
// Upload / download history + revert
// ---------------------------------------------------------------------------

export type ImportActor = { id: number; name: string; email: string } | null;

export type ImportLog = {
  id: number;
  entityKey: string;
  action: 'import' | 'export' | 'template';
  fileName: string | null;
  status: 'committed' | 'reverted' | 'failed';
  totalRows: number;
  successRows: number;
  errorRows: number;
  createdRows: number;
  updatedRows: number;
  performedBy: ImportActor;
  revertedAt: string | null;
  createdAt: string;
  _count?: { mutations: number };
};

export type ImportLogRowResult = {
  row: number;
  status: 'success' | 'error';
  action?: 'create' | 'update';
  recordId?: number;
  messages?: string[];
};

export type ImportLogDetail = ImportLog & {
  rowResults: ImportLogRowResult[] | null;
  revertedBy: ImportActor;
};

export const listImportLogs = (params: {
  entity?: string;
  action?: string;
  page?: number;
  limit?: number;
}) =>
  apiGet<{
    data: ImportLog[];
    metadata: { total: number; page: number; limit: number };
  }>('/master-data/import-logs', { params });

export const getImportLog = (id: number) =>
  apiGet<Envelope<ImportLogDetail>>(`/master-data/import-logs/${id}`);

export const revertImport = (id: number) =>
  apiPost<Envelope<{ importId: number; revertedMutations: number }>>(
    `/master-data/import-logs/${id}/revert`,
    {},
  );
